/**
 * Plain-text commands, shared by every way of using the ledger without the website: Discord
 * slash commands, a terminal (the CLI), and SMS later. "owe", "add 12.99 croissants for Emily,
 * Chloe", "paid Emily 20 via Zelle", "ask …". Changes come back as a proposal you confirm, the
 * same as the AI's, so nothing changes from a stray message.
 */
import { runTool, type ToolContext } from "../ai/tools.ts";
import type { Command } from "../commands.ts";
import { formatMicro } from "../money.ts";

export type TextResult =
  | { kind: "reply"; text: string }
  | { kind: "proposal"; text: string; commands: Command[] }
  | { kind: "ask"; question: string }
  | { kind: "error"; text: string };

export const TEXT_HELP = [
  "Commands:",
  "owe: who owes what",
  "owe Emily: one person's balance and unpaid items",
  "add 12.99 croissants for Emily, Chloe at Costco: add an item (asks you to confirm)",
  "paid Emily 20 via Zelle: log a payment from Emily (or: i paid Emily 20)",
  "ask <question>: ask the AI on your computer about the ledger",
].join("\n");

const MONEY = /^\$?(\d{1,7}(?:\.\d{1,2})?)$/;

function names(text: string): string[] {
  return text
    .split(/\s*(?:,|&|\band\b)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function fromTool(out: ReturnType<typeof runTool>): TextResult {
  if (out.kind === "error") return { kind: "error", text: out.message };
  if (out.kind === "proposal")
    return { kind: "proposal", text: out.summary, commands: out.commands };
  return { kind: "reply", text: JSON.stringify(out.data) };
}

function owe(ctx: ToolContext, who: string): TextResult {
  const L = ctx.ledger;
  if (who) {
    const q = who.toLowerCase();
    const p =
      L.people.find((x) => x.name.toLowerCase() === q) ??
      L.people.find((x) => x.name.toLowerCase().startsWith(q));
    if (!p)
      return {
        kind: "error",
        text: `There's no one called ${who} in ${ctx.state.name || "this group"}.`,
      };
    const lines = [
      p.balanceMicro > 0
        ? `${p.name} owes ${formatMicro(p.balanceMicro)}.`
        : p.balanceMicro < 0
          ? `You owe ${p.name} ${formatMicro(-p.balanceMicro)}.`
          : `${p.name} is all paid up.`,
    ];
    const unpaid = p.unpaid.slice(0, 8);
    for (const u of unpaid) lines.push(`- ${u.date} ${u.name}: ${formatMicro(u.remainingMicro)}`);
    if (p.unpaid.length > unpaid.length) lines.push(`…and ${p.unpaid.length - unpaid.length} more`);
    return { kind: "reply", text: lines.join("\n") };
  }
  const me = ctx.state.settings.ownerPersonId;
  const others = L.people.filter((p) => p.personId !== me);
  const owing = others
    .filter((p) => p.balanceMicro > 0)
    .sort((a, b) => b.balanceMicro - a.balanceMicro);
  const owed = others.filter((p) => p.balanceMicro < 0);
  if (!owing.length && !owed.length) return { kind: "reply", text: "Everyone is paid up." };
  const total = owing.reduce((s, p) => s + p.balanceMicro, 0);
  const lines = owing.map(
    (p) =>
      `${p.name}: ${formatMicro(p.balanceMicro)}${p.daysOverdue > 0 ? ` (${p.daysOverdue} days)` : ""}`,
  );
  for (const p of owed) lines.push(`You owe ${p.name}: ${formatMicro(-p.balanceMicro)}`);
  lines.push(`Total owed to you: ${formatMicro(total)}`);
  return { kind: "reply", text: lines.join("\n") };
}

function add(ctx: ToolContext, rest: string): TextResult {
  let text = ` ${rest.trim()}`;
  let store: string | undefined;
  let people: string[] = [];
  const at = /\s+at\s+(.+?)(?=\s+for\s+|$)/i.exec(text);
  if (at) {
    store = at[1]!.trim();
    text = text.replace(at[0], "");
  }
  const fr = /\s+for\s+(.+?)(?=\s+at\s+|$)/i.exec(text);
  if (fr) {
    people = names(fr[1]!);
    text = text.replace(fr[0], "");
  }
  const words = text.trim().split(/\s+/);
  // The price can come first ("add 12.99 croissants") or last ("add croissants 12.99").
  let price: string | undefined;
  if (MONEY.test(words[0] ?? "")) price = words.shift();
  else if (MONEY.test(words.at(-1) ?? "")) price = words.pop();
  const name = words.join(" ").trim();
  if (!price || !name) return { kind: "error", text: "Try: add 12.99 croissants for Emily, Chloe" };
  if (people.length === 1 && /^(everyone|all|everybody)$/i.test(people[0]!)) {
    people = Object.values(ctx.state.people)
      .filter((p) => !p.archived)
      .map((p) => p.name);
  }
  if (!people.length)
    return { kind: "error", text: `Say who it's for: add ${price} ${name} for Emily, Chloe` };
  return fromTool(
    runTool(
      "propose_add_item",
      { name, price: Number(MONEY.exec(price)![1]), people, ...(store ? { store } : {}) },
      ctx,
    ),
  );
}

function paid(ctx: ToolContext, rest: string, mine: boolean): TextResult {
  const m = /^(.+?)\s+\$?(\d{1,7}(?:\.\d{1,2})?)(?:\s+(?:via|by|with|on)\s+(.+))?$/i.exec(
    rest.trim(),
  );
  if (!m)
    return { kind: "error", text: mine ? "Try: i paid Emily 20" : "Try: paid Emily 20 via Zelle" };
  return fromTool(
    runTool(
      "propose_payment",
      {
        person: m[1]!.trim(),
        amount: Number(m[2]),
        direction: mine ? "i_paid_them" : "they_paid_me",
        ...(m[3] ? { note: m[3].trim() } : {}),
      },
      ctx,
    ),
  );
}

export function runText(input: string, ctx: ToolContext): TextResult {
  const text = input.trim().replace(/^\//, "");
  const [first = "", ...restWords] = text.split(/\s+/);
  const word = first.toLowerCase();
  const rest = restWords.join(" ");
  switch (word) {
    case "":
    case "help":
    case "?":
      return { kind: "reply", text: TEXT_HELP };
    case "owe":
    case "owes":
    case "balance":
    case "balances":
      return owe(ctx, rest.trim());
    case "add":
      return add(ctx, rest);
    case "paid":
      return paid(ctx, rest, false);
    case "i":
      if (/^paid\s/i.test(rest)) return paid(ctx, rest.replace(/^paid\s+/i, ""), true);
      break;
    case "ask":
      return rest.trim()
        ? { kind: "ask", question: rest.trim() }
        : { kind: "error", text: "Try: ask how much did we spend at Costco this month?" };
  }
  // "Emily paid 20" reads naturally too.
  const pm = /^(.+?)\s+paid\s+(.+)$/i.exec(text);
  if (pm && word !== "i") return paid(ctx, `${pm[1]} ${pm[2]}`, false);
  return { kind: "error", text: `I don't know "${first}". Send "help" for the list.` };
}
